import { getRecommendationsScope } from './recommendations-scope';

const STORAGE_PREFIX = 'fooda_rec_state';

export type DismissedItem = {
  itemId: string;
  recUuid: string;
};

type EventState = {
  addedAt: number;
  dismissedItems: DismissedItem[];
  addedFromRec: Record<string, string>;
  shownRecUuids: string[];
};

type StateData = {
  events: Record<string, EventState>;
};

const newEvent = (): EventState => ({
  addedAt: Date.now(),
  dismissedItems: [],
  addedFromRec: {},
  shownRecUuids: [],
});

const EMPTY: StateData = { events: {} };

export type RecAttributedItem = {
  id: string;
  recommendationTargetUuid?: string | null;
};

export type EventRecommendationsState = {
  getDismissedItemIds: () => string[];
  getDismissedItems: () => DismissedItem[];
  isItemDismissed: (itemId: string) => boolean;
  dismiss: (itemId: string, recUuid: string) => void;
  getAddedRecUuid: (itemId: string) => string | null;
  resolveRecUuid: (item: RecAttributedItem) => string | null;
  trackAdded: (itemId: string, recUuid: string) => void;
  clearAdded: (itemId?: string) => void;
  getShownRecUuids: () => string[];
  markShown: (recUuid: string) => boolean;
  invalidate: () => void;
};

export type RecommendationsState = {
  forEvent: (eventId: number | string) => EventRecommendationsState;
  purgeExpiredEvents: (ttlDays: number, now?: number) => string[];
};

type StateConfig = {
  getScope?: () => string;
};

const createRecommendationsState = ({
  getScope = getRecommendationsScope,
}: StateConfig = {}): RecommendationsState => {
  const getKey = (): string => `${STORAGE_PREFIX}.${getScope()}`;

  const read = (): StateData => {
    try {
      const raw = localStorage.getItem(getKey());
      if (!raw) return EMPTY;
      const parsed = JSON.parse(raw) as Partial<StateData>;
      return { events: parsed.events ?? {} };
    } catch {
      return EMPTY;
    }
  };

  const write = (data: StateData): void => {
    try {
      localStorage.setItem(getKey(), JSON.stringify(data));
    } catch {}
  };

  const readEvent = (eventId: number | string): EventState => {
    const data = read();
    const entry = data.events[String(eventId)];
    if (!entry) return newEvent();
    return {
      addedAt: entry.addedAt ?? Date.now(),
      dismissedItems: entry.dismissedItems ?? [],
      addedFromRec: entry.addedFromRec ?? {},
      shownRecUuids: entry.shownRecUuids ?? [],
    };
  };

  const writeEvent = (
    eventId: number | string,
    mutate: (current: EventState) => EventState
  ): void => {
    const data = read();
    const key = String(eventId);
    const existing = data.events[key];
    const current: EventState = existing
      ? {
          addedAt: existing.addedAt ?? Date.now(),
          dismissedItems: existing.dismissedItems ?? [],
          addedFromRec: existing.addedFromRec ?? {},
          shownRecUuids: existing.shownRecUuids ?? [],
        }
      : newEvent();
    const next = mutate(current);
    write({ events: { ...data.events, [key]: next } });
  };

  return {
    forEvent(eventId: number | string): EventRecommendationsState {
      return {
        getDismissedItems: () => readEvent(eventId).dismissedItems,
        getDismissedItemIds: () => readEvent(eventId).dismissedItems.map((d) => d.itemId),
        isItemDismissed: (itemId) =>
          readEvent(eventId).dismissedItems.some((d) => d.itemId === itemId),
        dismiss: (itemId, recUuid) =>
          writeEvent(eventId, (current) =>
            current.dismissedItems.some((d) => d.itemId === itemId)
              ? current
              : { ...current, dismissedItems: [...current.dismissedItems, { itemId, recUuid }] }
          ),
        getAddedRecUuid: (itemId) => readEvent(eventId).addedFromRec[itemId] ?? null,
        resolveRecUuid: (item) =>
          readEvent(eventId).addedFromRec[item.id] ?? item.recommendationTargetUuid ?? null,
        trackAdded: (itemId, recUuid) =>
          writeEvent(eventId, (current) => ({
            ...current,
            addedFromRec: { ...current.addedFromRec, [itemId]: recUuid },
          })),
        clearAdded: (itemId) =>
          writeEvent(eventId, (current) => {
            if (!itemId) return { ...current, addedFromRec: {} };
            const { [itemId]: _omit, ...rest } = current.addedFromRec;
            return { ...current, addedFromRec: rest };
          }),
        getShownRecUuids: () => readEvent(eventId).shownRecUuids,
        markShown: (recUuid) => {
          if (!recUuid) return false;
          const current = readEvent(eventId);
          if (current.shownRecUuids.includes(recUuid)) return false;
          writeEvent(eventId, (state) => ({
            ...state,
            shownRecUuids: [...state.shownRecUuids, recUuid],
          }));
          return true;
        },
        invalidate: () => {
          const data = read();
          const key = String(eventId);
          if (!(key in data.events)) return;
          const { [key]: _omit, ...rest } = data.events;
          write({ events: rest });
        },
      };
    },

    purgeExpiredEvents(ttlDays, now = Date.now()) {
      const data = read();
      const cutoff = now - ttlDays * 24 * 60 * 60 * 1000;
      const purged: string[] = [];
      const kept: Record<string, EventState> = {};
      for (const [eventId, event] of Object.entries(data.events)) {
        if (typeof event.addedAt !== 'number' || event.addedAt < cutoff) {
          purged.push(eventId);
          continue;
        }
        kept[eventId] = event;
      }
      if (purged.length) write({ events: kept });
      return purged;
    },
  };
};

export const recommendationsState = createRecommendationsState();
